/**
 * Billing → Manual billing: a statement for an old project that was closed
 * before SF and is still waiting to be collected. Pick the customer, type the
 * lines, preview, create. No lead is needed; the statement then behaves like
 * any other (issue, PDF, collections, AR ageing).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, FileText } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import { Alert, Button, Card, Field, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { EMPTY_LINE, LineItemsEditor, filledLines, linesTotal, type DraftLine } from '../purchasing/LineItemsEditor';

interface CustomerOption { id: string; code: string; name: string; tin: string | null; isGovernment: boolean }
interface ExistingBilling {
  id: string; code: string; billingDate: string; total: string; settledAmount: string; status: string;
  salesOrder: { code: string; projectName: string; source: string; legacyRef: string | null };
}
interface Preview {
  dryRun: boolean;
  customer: { name: string };
  projectName: string;
  legacyRef: string | null;
  contractAmountVatEx: string;
  statement: { lines: number; amountVatEx: string; vat: string; total: string };
  billingDate: string;
  paymentTermsDays: number;
  billing?: { id: string; code: string; status: string; total: string };
}

const todayManila = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());

export function ManualBillingPage(): JSX.Element {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customerId: '', projectName: '', legacyRef: '', clientPoRef: '', contractDate: '', contractAmount: '',
    vatApplicable: true, taxClass: 'GOODS', paymentTermsDays: '30', billingDate: todayManila(), remarks: '', issueNow: false,
  });
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof form>): void => { setForm({ ...form, ...patch }); setPreview(null); };

  const customers = useQuery({
    queryKey: ['customers', 'manual-billing'],
    queryFn: () => api<{ data: CustomerOption[] }>('/customers?pageSize=200&status=ACTIVE&sort=name'),
  });
  const existing = useQuery({
    queryKey: ['manual-billing', 'customer', form.customerId],
    queryFn: () => api<{ data: ExistingBilling[] }>(`/billings/manual/customer/${form.customerId}`),
    enabled: Boolean(form.customerId),
  });
  const customer = useMemo(() => customers.data?.data.find((c) => c.id === form.customerId) ?? null, [customers.data, form.customerId]);

  const body = (dryRun: boolean) => ({
    customerId: form.customerId,
    projectName: form.projectName.trim(),
    legacyRef: form.legacyRef.trim() || undefined,
    clientPoRef: form.clientPoRef.trim() || undefined,
    contractDate: form.contractDate || undefined,
    contractAmountVatEx: form.contractAmount ? Number(form.contractAmount) : undefined,
    vatApplicable: form.vatApplicable,
    taxClass: form.taxClass,
    paymentTermsDays: Number(form.paymentTermsDays || '30'),
    billingDate: form.billingDate || undefined,
    items: filledLines(lines).map((l) => ({ particular: l.particular.trim(), unit: l.unit.trim() || undefined, qty: l.qty || '1', unitPrice: l.unitPrice || '0' })),
    remarks: form.remarks.trim() || undefined,
    issueNow: form.issueNow,
    dryRun,
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<{ data: Preview }>('/billings/manual', { method: 'POST', body: body(dryRun) }),
    onSuccess: (result) => {
      setError(null);
      if (result.data.billing) navigate(`/billing/${result.data.billing.id}`);
      else setPreview(result.data);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not create the statement.'),
  });

  const total = linesTotal(lines);
  const ready = Boolean(form.customerId && form.projectName.trim().length >= 2 && filledLines(lines).length && total > 0);

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="space-y-1">
        <Link to="/billing" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy dark:hover:text-white">
          <ArrowLeft aria-hidden="true" className="h-4 w-4" /> Billing
        </Link>
        <h1 className="text-xl font-bold text-navy dark:text-white">Manual billing</h1>
        <p className="text-sm text-slate-500">
          For old projects that were closed outside SF and are still waiting to be collected. No lead is
          needed. Old projects do not count as sales of this period and are left out of profitability.
        </p>
      </header>

      {error && <Alert tone="critical">{error}</Alert>}

      <Card title="Customer and project">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer" required>
            <select className={inputClass} value={form.customerId} onChange={(e) => set({ customerId: e.target.value })}>
              <option value="">Choose a customer</option>
              {customers.data?.data.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
            </select>
          </Field>
          <Field label="Project name" required hint="As the customer knows it">
            <input className={inputClass} value={form.projectName} maxLength={200} onChange={(e) => set({ projectName: e.target.value })} />
          </Field>
          <Field label="Old reference" hint="Old contract, project or SOA number; SF refuses the same one twice for a customer">
            <input className={inputClass} value={form.legacyRef} maxLength={80} onChange={(e) => set({ legacyRef: e.target.value })} />
          </Field>
          <Field label="Client PO">
            <input className={inputClass} value={form.clientPoRef} maxLength={80} onChange={(e) => set({ clientPoRef: e.target.value })} />
          </Field>
          <Field label="Contract date" hint="When the project was contracted; blank = billing date">
            <input type="date" className={inputClass} value={form.contractDate} onChange={(e) => set({ contractDate: e.target.value })} />
          </Field>
          <Field label="Whole contract, VAT-ex" hint="Blank = this statement only. Fill in if more statements will follow.">
            <input type="number" min={0} step="0.01" className={inputClass} value={form.contractAmount} onChange={(e) => set({ contractAmount: e.target.value })} />
          </Field>
        </div>
        {customer && existing.data && existing.data.data.length > 0 && (
          <div className="mt-4">
            <p className="mb-1 text-sm font-medium">Already billed to {customer.name}</p>
            <TableWrap>
              <thead><tr><Th>Statement</Th><Th>Project</Th><Th>Date</Th><Th align="right">Total</Th><Th>Status</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {existing.data.data.slice(0, 8).map((b) => (
                  <tr key={b.id}>
                    <Td><Link className="font-mono text-xs text-brand-700 hover:underline dark:text-brand-100" to={`/billing/${b.id}`}>{b.code}</Link></Td>
                    <Td><span className="text-sm">{b.salesOrder.projectName}</span>{b.salesOrder.legacyRef && <span className="block text-xs text-slate-500">{b.salesOrder.legacyRef}</span>}</Td>
                    <Td>{shortDate(b.billingDate)}</Td>
                    <Td align="right">{peso(b.total)}</Td>
                    <Td><StatusChip tone={b.status === 'PAID' ? 'ok' : b.status === 'VOID' ? 'neutral' : 'info'}>{b.status.replace('_', ' ').toLowerCase()}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </div>
        )}
      </Card>

      <Card title="Statement">
        <div className="grid gap-4 sm:grid-cols-4">
          <Field label="Billing date" required>
            <input type="date" className={inputClass} value={form.billingDate} onChange={(e) => set({ billingDate: e.target.value })} />
          </Field>
          <Field label="Terms (days)">
            <input type="number" min={0} max={365} className={inputClass} value={form.paymentTermsDays} onChange={(e) => set({ paymentTermsDays: e.target.value })} />
          </Field>
          <Field label="Tax class" hint={customer?.isGovernment ? 'Government customer: withholding VAT applies' : undefined}>
            <select className={inputClass} value={form.taxClass} onChange={(e) => set({ taxClass: e.target.value })}>
              <option value="GOODS">Goods</option><option value="SERVICES">Services</option><option value="MIXED">Mixed</option>
            </select>
          </Field>
          <Field label="VAT">
            <label className="flex min-h-[42px] items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={form.vatApplicable} onChange={(e) => set({ vatApplicable: e.target.checked })} /> Add 12% VAT
            </label>
          </Field>
        </div>
        <div className="mt-4">
          <LineItemsEditor lines={lines} onChange={(l) => { setLines(l); setPreview(null); }} vatIncluded={false} label="Statement lines (VAT-exclusive)" />
        </div>
        <div className="mt-4">
          <Field label="Remarks" hint="Printed on the statement">
            <textarea className={`${inputClass} min-h-16`} value={form.remarks} maxLength={1000} onChange={(e) => set({ remarks: e.target.value })} />
          </Field>
        </div>
        {can('billing.issue') && (
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={form.issueNow} onChange={(e) => set({ issueNow: e.target.checked })} /> Issue it now (otherwise it is saved as a draft)
          </label>
        )}
      </Card>

      {preview && (
        <Alert tone="info">
          {preview.customer.name} · {preview.projectName}{preview.legacyRef ? ` (${preview.legacyRef})` : ''}: {preview.statement.lines} line{preview.statement.lines === 1 ? '' : 's'},{' '}
          {peso(preview.statement.amountVatEx)} + VAT {peso(preview.statement.vat)} = <strong>{peso(preview.statement.total)}</strong>, dated {shortDate(preview.billingDate)}, due in {preview.paymentTermsDays} days.
          {' '}Contract on file: {peso(preview.contractAmountVatEx)} VAT-ex.
        </Alert>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" disabled={!ready || run.isPending} onClick={() => run.mutate(true)}>
          <Eye aria-hidden="true" className="h-4 w-4" /> Preview
        </Button>
        <Button disabled={!ready || !preview || run.isPending} busy={run.isPending && Boolean(preview)} onClick={() => run.mutate(false)}>
          <FileText aria-hidden="true" className="h-4 w-4" /> {form.issueNow ? 'Create and issue' : 'Create draft statement'}
        </Button>
      </div>
    </div>
  );
}
